import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import GeneralSupportSidebar from './GeneralSupportSidebar';
import LayoutWithSidebar from '../common/layoutWithSidebar';
import InitialAnalytics from '../UserJourny/InitialAnalytics';
import AssistantDefination from '../UserJourny/AssistantDefination';
import Personalization from '../UserJourny/Personalization';
// import FaqSetup from './FaqSetup';
import { useLocation, useNavigate } from 'react-router';
import { useSaveAssistant } from '../../hooks/useSaveAssistant';
import { useUser } from '../../hooks/useUser';
import { useAgents } from '../../hooks/useAgents';
import { assistantDefinitionSchema } from '../../schemas/userJourney/assistantDefination';
import { personalizeSchema } from '../../schemas/userJourney/personalize';
import { RootState } from '../../utils/redux/redux';
import { useSelector } from 'react-redux';
import NewFaqSetup from './NewFaqSetup';

const GeneralSupportFlow = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedTemplate, setSelectedTemplate] = useState<any>(
    location.state?.selectedTemplate || null
  );
  const [selectedSection, setSelectedSection] = useState<string>('faq-setup');
  const [selectedSubSection, setSelectedSubSection] =
    useState<string>('select-preference');

  const { saveAssistant, isPending } = useSaveAssistant();
  const { user } = useUser();
  const { agents, isLoading } = useAgents();

  const { assistantDefinition, personalization } = useSelector(
    (state: RootState) => state.userJourney
  );

  // Add this to track the current agent
  const currentAgent = agents?.agents?.[agents?.agents?.length - 1];

  const handleSectionChange = (section: string, subSection?: string) => {
    setSelectedSection(section);
    if (subSection) {
      setSelectedSubSection(subSection);
    } else {
      // Set default subSection for each section
      const defaultSubSections: Record<string, string> = {
        'faq-setup': 'select-preference',
        'assistant-definition': 'assistant-name',
        personalization: 'greeting',
        analytics: 'call-stage',
      };
      setSelectedSubSection(defaultSubSections[section]);
    }
  };

  useEffect(() => {
    // Set template from location state if available
    if (location.state?.selectedTemplate) {
      setSelectedTemplate(location.state.selectedTemplate);
    }
  }, [location.state]);

  const handleNext = async () => {
    const sections = [
      'faq-setup',
      'assistant-definition',
      'personalization',
      'analytics',
    ];
    const currentIndex = sections.indexOf(selectedSection);

    // Save data before moving to next section
    if (
      selectedSection === 'assistant-definition' ||
      selectedSection === 'personalization'
    ) {
      try {
        if (selectedSection === 'assistant-definition') {
          await assistantDefinitionSchema.validate(assistantDefinition.data, {
            abortEarly: false,
          });
        } else if (selectedSection === 'personalization') {
          await personalizeSchema.validate(personalization.data, {
            abortEarly: false,
          });
        }
        await saveAssistant({
          userId: user?._id,
          status: 'draft',
          assistantId: agents?.agents[agents.agents.length - 1]._id,
          section:
            selectedSection === 'assistant-definition'
              ? 'assistant-definition'
              : 'personalization',
        });
      } catch (error) {
        console.error('Error saving assistant data:', error);
        return;
      }
    }

    // Proceed to next section
    if (currentIndex < sections.length - 1) {
      handleSectionChange(sections[currentIndex + 1]);
    }
  };

  const handlePrev = () => {
    const sections = [
      'faq-setup',
      'assistant-definition',
      'personalization',
      'analytics',
    ];
    const currentIndex = sections.indexOf(selectedSection);
    if (currentIndex > 0) {
      handleSectionChange(sections[currentIndex - 1]);
    } else {
      // If we're at the first section, navigate back to the landing page
      navigate('/add-new-assistant');
    }
  };

  const formatSectionTitle = (section: string) => {
    return section
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const renderContent = () => {
    // Don't render FaqSetup until we have the current agent
    if (selectedSection === 'faq-setup' && (!currentAgent || isLoading)) {
      return null; // Or a loading spinner
    }

    switch (selectedSection) {
      case 'faq-setup':
        return (
          // <FaqSetup
          //   onNext={handleNext}
          //   onPrev={handlePrev}
          //   assistant={currentAgent}
          //   userId={user?._id}
          // />
          <NewFaqSetup onNext={handleNext} onPrev={handlePrev} />
        );
      case 'assistant-definition':
        return (
          <AssistantDefination
            onNext={handleNext}
            onPrev={handlePrev}
            isPending={isPending}
            isGeneralSupportFlow={true}
          />
        );
      case 'personalization':
        return (
          <Personalization
            onNext={handleNext}
            onPrev={handlePrev}
            selectedSubSection={selectedSubSection}
            isPending={isPending}
          />
        );
      case 'analytics':
        return (
          <InitialAnalytics
            onPrev={handlePrev}
            shouldShowMenuConfigFlow={false}
          />
        );
      default:
        return null;
    }
  };

  return (
    <LayoutWithSidebar>
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'white',
          py: 0.2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderBottom: '1px solid #E0E0E0',
            p: 2,
            backgroundColor: 'white',
          }}
        >
          <Typography
            variant="h6"
            color="custom.textBlue"
            sx={{ textAlign: 'center' }}
          >
            <span
              style={{
                fontSize: '16px',
                fontWeight: 400,
                color: 'lightGray.text',
              }}
            >
              {selectedTemplate?.name}
            </span>
            <span
              style={{
                fontSize: '16px',
                margin: '0 8px',
                color: 'custom.textBlue',
              }}
            >
              /
            </span>
            <span
              style={{
                fontSize: '16px',
                fontWeight: 500,
                color: 'custom.textBlue',
              }}
            >
              {selectedSection && formatSectionTitle(selectedSection)}
            </span>
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            height: 'calc(100vh - 64px)',
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <GeneralSupportSidebar
            templateName="General Support"
            selectedSection={selectedSection}
            selectedSubSection={selectedSubSection}
            onSectionChange={handleSectionChange}
          />

          <Box sx={{ flex: 1, overflow: 'auto' }}>{renderContent()}</Box>
        </Box>
      </Box>
    </LayoutWithSidebar>
  );
};

export default GeneralSupportFlow;
